/**
 * Tab Store (Zustand)
 *
 * Manages multiple independent editor tabs. Each tab holds its own
 * pipeline, input/output, and visualizer state. The tool registry and
 * user preferences are shared across all tabs.
 *
 * Phase 2 — Unit 2.1
 *
 * @see docs/exec-plans/active/phases/02-tool-tabs.md
 */

import { create } from 'zustand';
import { createJSONStorage,persist } from 'zustand/middleware';

import type { IPipelineState,ITabState } from '@/interfaces/IPipeline';
import { DEFAULT_PANEL_CONFIG } from '@/lib/storeConstants';

// ─────────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────────

let tabCounter = 0;

/**
 * Generate a unique tab ID. Deterministic prefix for readability,
 * random suffix for uniqueness.
 */
function generateTabId(): string {
  tabCounter += 1;
  return `tab-${tabCounter}-${Math.random().toString(36).substring(2, 7)}`;
}

/** Default empty pipeline state. */
const EMPTY_PIPELINE_STATE: IPipelineState = {
  pipeline: [],
  input: '',
  output: '',
  inputType: null,
  inputTypeMode: 'auto',
  stepResults: [],
  pipelineVariables: {},
  activePipelineName: null,
  activePipelineDescription: null,
  activePipelineId: null,
  isProcessing: false,
};

/**
 * Create a fresh empty tab state.
 */
function createEmptyTab(name?: string, status: 'selecting' | 'active' = 'selecting'): ITabState {
  return {
    id: generateTabId(),
    name: name ?? `Tab ${tabCounter}`,
    inputPanelConfig: { ...DEFAULT_PANEL_CONFIG, modifiers: { ...DEFAULT_PANEL_CONFIG.modifiers } },
    outputPanelConfig: { ...DEFAULT_PANEL_CONFIG, modifiers: { ...DEFAULT_PANEL_CONFIG.modifiers } },
    isFlipped: false,
    color: '',
    pinned: false,
    status,
    ...EMPTY_PIPELINE_STATE,
  };
}

/** Deduplicate a tab name against existing tabs. "Foo" → "Foo 2" → "Foo 3" etc. */
function deduplicateName(baseName: string, existingTabs: ITabState[]): string {
  const names = new Set(existingTabs.map((t) => t.name));
  if (!names.has(baseName)) return baseName;
  let n = 2;
  while (names.has(`${baseName} ${n}`)) n++;
  return `${baseName} ${n}`;
}

// ─────────────────────────────────────────────────────────────────────────────
// Store interface
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Project a tab to its persisted shape (whitelist approach).
 * Only metadata and pipeline config are saved; raw user data
 * (input/output/stepResults) are excluded unless the user
 * has explicitly opted in via `persistInput`.
 */
function toPersistedTab(t: ITabState): Partial<ITabState> {
  const keepIO = Boolean(t.persistInput);
  return {
    // Identity
    id: t.id,
    name: t.name,
    status: t.status,
    color: t.color,
    pinned: t.pinned,
    isFlipped: t.isFlipped,
    persistInput: t.persistInput,
    // Pipeline config
    pipeline: t.pipeline,
    pipelineVariables: t.pipelineVariables,
    activePipelineName: t.activePipelineName,
    activePipelineDescription: t.activePipelineDescription,
    activePipelineId: t.activePipelineId,
    activePipelineNameIsAuto: t.activePipelineNameIsAuto,
    activePipelineDescriptionIsAuto: t.activePipelineDescriptionIsAuto,
    // Visualizer
    inputPanelConfig: t.inputPanelConfig,
    outputPanelConfig: t.outputPanelConfig,
    inputActiveModes: t.inputActiveModes,
    outputActiveModes: t.outputActiveModes,
    // User preference
    inputTypeMode: t.inputTypeMode,
    // Opt-in user data (stripped by default)
    input: keepIO ? t.input : '',
    output: keepIO ? t.output : '',
    stepResults: keepIO ? t.stepResults : [],
    // Runtime fields always reset
    isProcessing: false,
    inputType: null,
  };
}

interface TabStoreState {
  /** Ordered list of all open tabs */
  tabs: ITabState[];
  /** ID of the currently active tab */
  activeTabId: string;

  // ── Actions ──────────────────────────────────────────────────────────────

  /** Create a new empty tab and switch to it. Returns the new tab's ID. */
  createTab: (name?: string) => string;
  /** Close a tab by ID. If the last tab is closed, a new empty tab is created. */
  closeTab: (tabId: string) => void;
  /** Switch to an existing tab by ID. No-op if the tab doesn't exist. */
  switchTab: (tabId: string) => void;
  /** Reorder a tab from one index to another. */
  reorderTabs: (fromIndex: number, toIndex: number) => void;
  /** Rename a tab. No-op if the tab doesn't exist. */
  renameTab: (tabId: string, name: string) => void;
  /** Duplicate a tab. Returns the new tab's ID. */
  duplicateTab: (tabId: string) => string;
  /** Get the current active tab state. */
  getActiveTab: () => ITabState;
  /** Update fields of the active tab (partial merge). */
  updateActiveTab: (patch: Partial<ITabState>) => void;
  /** Reset to a single empty tab. */
  reset: () => void;
  /** Set a tab's lifecycle status. */
  setTabStatus: (tabId: string, status: 'selecting' | 'active') => void;
  /** Toggle whether a tab persists input/output/stepResults across reloads. */
  togglePersistInput: (tabId: string) => void;
}

// ─────────────────────────────────────────────────────────────────────────────
// Store
// ─────────────────────────────────────────────────────────────────────────────

const initialTab = createEmptyTab(undefined, 'active');

export const useTabStore = create<TabStoreState>()(
  persist(
    (set, get) => ({
      tabs: [initialTab],
      activeTabId: initialTab.id,

      createTab: (name?: string) => {
        const { tabs } = get();
        const baseName = name ?? `Tab ${tabs.length + 1}`;
        const newTab = createEmptyTab(deduplicateName(baseName, tabs));
        set((state) => ({
          tabs: [...state.tabs, newTab],
          activeTabId: newTab.id,
        }));
        return newTab.id;
      },

      closeTab: (tabId: string) => {
        const { tabs, activeTabId } = get();
        const index = tabs.findIndex((t) => t.id === tabId);
        if (index === -1) return;

        const remaining = tabs.filter((t) => t.id !== tabId);

        // If that was the last tab, create a fresh one
        if (remaining.length === 0) {
          const fresh = createEmptyTab(undefined, 'active');
          set({ tabs: [fresh], activeTabId: fresh.id });
          return;
        }

        // If we closed the active tab, switch to an adjacent one
        let nextActiveId = activeTabId;
        if (activeTabId === tabId) {
          // Prefer the tab to the right, fall back to the one to the left
          const nextIndex = Math.min(index, remaining.length - 1);
          nextActiveId = remaining[nextIndex].id;
        }

        set({ tabs: remaining, activeTabId: nextActiveId });
      },

      switchTab: (tabId: string) => {
        const { tabs } = get();
        if (tabs.some((t) => t.id === tabId)) {
          set({ activeTabId: tabId });
        }
      },

      reorderTabs: (fromIndex: number, toIndex: number) => {
        const { tabs } = get();
        if (
          fromIndex < 0 || fromIndex >= tabs.length ||
          toIndex < 0 || toIndex >= tabs.length ||
          fromIndex === toIndex
        ) return;

        const next = [...tabs];
        const [moved] = next.splice(fromIndex, 1);
        next.splice(toIndex, 0, moved);
        set({ tabs: next });
      },

      renameTab: (tabId: string, name: string) => {
        set((state) => ({
          tabs: state.tabs.map((t) =>
            t.id === tabId ? { ...t, name } : t
          ),
        }));
      },

      duplicateTab: (tabId: string) => {
        const { tabs } = get();
        const source = tabs.find((t) => t.id === tabId);
        if (!source) {
          // If source not found, just create an empty tab
          return get().createTab();
        }

        const newTab: ITabState = {
          ...structuredClone(source),
          id: generateTabId(),
          name: `${source.name} (Copy)`,
          isProcessing: false,
          status: 'active',
        };

        set((state) => ({
          tabs: [...state.tabs, newTab],
          activeTabId: newTab.id,
        }));
        return newTab.id;
      },

      getActiveTab: () => {
        const { tabs, activeTabId } = get();
        return tabs.find((t) => t.id === activeTabId) ?? tabs[0];
      },

      updateActiveTab: (patch: Partial<ITabState>) => {
        const { activeTabId } = get();
        set((state) => ({
          tabs: state.tabs.map((t) =>
            t.id === activeTabId ? { ...t, ...patch } : t
          ),
        }));
      },

      reset: () => {
        const fresh = createEmptyTab(undefined, 'active');
        set({ tabs: [fresh], activeTabId: fresh.id });
      },

      setTabStatus: (tabId: string, status: 'selecting' | 'active') => {
        set((state) => ({
          tabs: state.tabs.map((t) =>
            t.id === tabId ? { ...t, status } : t
          ),
        }));
      },

      togglePersistInput: (tabId: string) => {
        set((state) => ({
          tabs: state.tabs.map((t) =>
            t.id === tabId ? { ...t, persistInput: !t.persistInput } : t
          ),
        }));
      },
    }),
    {
      name: 'swedevtools-tabs',
      version: 2,
      migrate: (persisted, _version) => {
        const state = persisted as Record<string, unknown>;
        // v1→v2: switch from spread-all to whitelist persistence.
        // Strip inputType (runtime-derived) and isProcessing (runtime).
        // Preserve input/output/stepResults only when persistInput is true.
        if (Array.isArray(state.tabs)) {
          state.tabs = (state.tabs as Record<string, unknown>[]).map((t: Record<string, unknown> | null | undefined) => {
            const keepIO = Boolean(t?.persistInput);
            return {
              id: typeof t?.id === 'string' ? t.id : generateTabId(),
              name: typeof t?.name === 'string' ? t.name : `Tab`,
              pipeline: Array.isArray(t?.pipeline) ? t.pipeline : [],
              input: keepIO && typeof t?.input === 'string' ? t.input : '',
              output: keepIO && typeof t?.output === 'string' ? t.output : '',
              inputType: null,
              inputTypeMode: (t?.inputTypeMode === 'auto' || t?.inputTypeMode === 'manual') ? t.inputTypeMode : 'auto',
              stepResults: keepIO && Array.isArray(t?.stepResults) ? t.stepResults : [],
              pipelineVariables: (t?.pipelineVariables && typeof t.pipelineVariables === 'object' && !Array.isArray(t.pipelineVariables)) ? t.pipelineVariables : {},
              activePipelineName: typeof t?.activePipelineName === 'string' ? t.activePipelineName : null,
              activePipelineDescription: typeof t?.activePipelineDescription === 'string' ? t.activePipelineDescription : null,
              activePipelineId: typeof t?.activePipelineId === 'string' ? t.activePipelineId : null,
              isProcessing: false,
              inputPanelConfig: (t?.inputPanelConfig && typeof t.inputPanelConfig === 'object') ? t.inputPanelConfig : { ...DEFAULT_PANEL_CONFIG, modifiers: { ...DEFAULT_PANEL_CONFIG.modifiers } },
              outputPanelConfig: (t?.outputPanelConfig && typeof t.outputPanelConfig === 'object') ? t.outputPanelConfig : { ...DEFAULT_PANEL_CONFIG, modifiers: { ...DEFAULT_PANEL_CONFIG.modifiers } },
              isFlipped: typeof t?.isFlipped === 'boolean' ? t.isFlipped : false,
              color: typeof t?.color === 'string' ? t.color : '',
              pinned: typeof t?.pinned === 'boolean' ? t.pinned : false,
              status: (t?.status === 'selecting' || t?.status === 'active') ? t.status : 'active',
              persistInput: typeof t?.persistInput === 'boolean' ? t.persistInput : false,
              activePipelineNameIsAuto: typeof t?.activePipelineNameIsAuto === 'boolean' ? t.activePipelineNameIsAuto : undefined,
              activePipelineDescriptionIsAuto: typeof t?.activePipelineDescriptionIsAuto === 'boolean' ? t.activePipelineDescriptionIsAuto : undefined,
              inputActiveModes: Array.isArray(t?.inputActiveModes) ? t.inputActiveModes : undefined,
              outputActiveModes: Array.isArray(t?.outputActiveModes) ? t.outputActiveModes : undefined,
            };
          });
          if ((state.tabs as unknown[]).length === 0) {
            state.tabs = [createEmptyTab(undefined, 'active')];
          }
        } else {
          state.tabs = [createEmptyTab(undefined, 'active')];
        }
        // Ensure activeTabId is valid
        const tabs = state.tabs as { id: string }[];
        if (!tabs.some((t) => t.id === state.activeTabId)) {
          state.activeTabId = tabs[0].id;
        }
        return state as unknown as TabStoreState;
      },
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        tabs: state.tabs.map((t) => toPersistedTab(t)),
        activeTabId: state.activeTabId,
      }),
    }
  )
);

/**
 * Reset the tab counter — used in tests only.
 */
export function _resetTabCounter() {
  tabCounter = 0;
}
